import { useCallback, useMemo, useState } from 'react'
import { StakeListContext } from '../context'
import { useInventory } from '../hooks/useInventory'

const DEFAULT_FILTER: StakeFilter = {
    category: 'all',
    status: 'all',
    search: '',
    sort: 'id',
}

const TYPE_ORDER = ['ape', 'droid', 'lootbox', 'weapon']

const byType = (a: NFT, b: NFT) =>
    TYPE_ORDER.indexOf(a.operative_type) - TYPE_ORDER.indexOf(b.operative_type)

/**
 * ID sorts group by type first. `operative_id` is only unique within a type, so sorting a mixed
 * list purely by id interleaves apes, droids and weapons into meaningless runs. With a single
 * category selected the type key is constant and this degrades to plain id order.
 *
 * Rate sorts stay pure — rate is the thing being asked about — and tie-break for stable output.
 */
const comparators: Record<InventorySort, (a: NFT, b: NFT) => number> = {
    id: (a, b) => byType(a, b) || a.operative_id - b.operative_id,
    '-id': (a, b) => byType(a, b) || b.operative_id - a.operative_id,
    rate: (a, b) => a.stake_rate - b.stake_rate || byType(a, b) || a.operative_id - b.operative_id,
    '-rate': (a, b) => b.stake_rate - a.stake_rate || byType(a, b) || a.operative_id - b.operative_id,
}

/**
 * Remembered across visits, because the choice between grid and list is a standing preference
 * rather than a per-session one. Defaults to list on a phone and grid on a desktop: a 375px screen
 * shows five cards in the grid and roughly twice that as rows.
 */
const VIEW_KEY = 'inventory:view'

const initialView = (): InventoryView => {
    try {
        const saved = localStorage.getItem(VIEW_KEY)
        if (saved === 'grid' || saved === 'list') return saved
    } catch {
        // Private-mode Safari throws on localStorage; the default below is fine.
    }
    return window.matchMedia?.('(max-width: 639px)').matches ? 'list' : 'grid'
}

export const StakeListProvider = ({ children }) => {
    const { data } = useInventory()
    const [filter, setFilter] = useState<StakeFilter>(DEFAULT_FILTER)
    const [view, setViewState] = useState<InventoryView>(initialView)

    const setView = useCallback((next: InventoryView) => {
        setViewState(next)
        try {
            localStorage.setItem(VIEW_KEY, next)
        } catch {}
    }, [])

    const setCategory = useCallback((category: InventoryCategory) => setFilter((f) => ({ ...f, category })), [])
    const setStatus = useCallback((status: InventoryStatus) => setFilter((f) => ({ ...f, status })), [])
    const setSearch = useCallback((search: string) => setFilter((f) => ({ ...f, search })), [])
    const setSort = useCallback((sort: InventorySort) => setFilter((f) => ({ ...f, sort })), [])

    // Category + search define the "scope": what the status counts are counted out of. Keeping
    // this separate from `data` is what lets ACTIVE [n] / INACTIVE [n] stay truthful while a
    // status is selected — otherwise each count would only ever describe itself.
    const scope = useMemo(() => {
        const search = filter.search.trim().replace(/^#/, '').toLowerCase()
        return data.filter((nft) => {
            if (filter.category !== 'all' && nft.operative_type !== filter.category) return false
            if (search && !String(nft.operative_id).includes(search)) return false
            return true
        })
    }, [data, filter.category, filter.search])

    const visible = useMemo(() => {
        const byStatus =
            filter.status === 'all' ? scope : scope.filter((nft) => !!nft.staked === (filter.status === 'active'))
        return [...byStatus].sort(comparators[filter.sort])
    }, [scope, filter.status, filter.sort])

    return (
        <StakeListContext.Provider
            value={{ data: visible, scope, filter, setCategory, setStatus, setSearch, setSort, view, setView }}
        >
            {children}
        </StakeListContext.Provider>
    )
}
